<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'autoReadings',
    path: '/auto-readings',
    component: 'SensorDataBrowser',
    title: () => pgettext('MeterSensorDataBrowser', 'Auto Readings'),
    icon: 'fa-ethernet',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'SensorDataBrowser',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getQueryParameters = (...args) => {
    return { time: timeTo.value };
  };
  const onCreateMeterReadingsPayload = (...args) => {
    return $imodal.value
      .show({ name: 'CreateMeterReadingsParameters' })
      .then((result) => {
        if (!result || result.cancel || !result.value) {
          return;
        }
        return {
          ...result.value,
          readings: selectedItems.value
            .filter((p) => !!p.meter_id)
            .map((p) => ({
              meter_id: p.meter_id,
              serial: p.meter_serial,
              value: p.value,
              unit: p.unit,
              reading_type: 'automated',
              reading_date: $fn.value.formatDate(
                $fn.value.parseISODate(p.timestamp),
                'yyyy-MM-dd'
              ),
              reading_time: $fn.value.formatDate(
                $fn.value.parseISODate(p.timestamp),
                'hh:mm'
              ),
            })),
        };
      });
  };

  const selectedItems = ref([]);
  const timeTo = ref(null);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'meter_type',
      name: $fn.value.pgettext('MeterSensorDataBrowser', 'Meter Type'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'gettext',
            context: 'MeterTypes',
            conversion: 'StartCase',
            filterInput: true,
          },
        ],
      },
    },
    {
      field: 'meter_serial',
      name: $fn.value.pgettext('MeterSensorDataBrowser', 'Meter'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'meter',
        params: {
          id: 'meter_id',
        },
      },
    },
    {
      field: 'tag_id',
      name: $fn.value.pgettext('MeterSensorDataBrowser', 'Meter Tag'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'meter',
        params: {
          id: 'meter_id',
        },
      },
    },
    {
      field: 'sensor',
      name: $fn.value.pgettext('MeterSensorDataBrowser', 'Sensor Key'),
    },
    {
      field: 'raw',
      name: $fn.value.pgettext('MeterSensorDataBrowser', 'Raw'),
      textAlign: 'right',
      formatter: {
        name: 'number',
      },
      cssClass: 'text-muted',
    },
    {
      field: 'offset',
      name: $fn.value.pgettext('MeterSensorDataBrowser', 'Offset'),
      textAlign: 'right',
      formatter: {
        name: 'number',
      },
      cssClass: 'text-muted',
    },
    {
      field: 'value',
      name: $fn.value.pgettext('MeterSensorDataBrowser', 'Value'),
      textAlign: 'right',
      cssClass: 'fw-bold',
      formatter: {
        name: 'number',
      },
    },
    {
      field: 'unit',
      name: $fn.value.pgettext('MeterSensorDataBrowser', 'Unit'),
      cssClass: 'fw-bold',
    },
    {
      field: 'timestamp',
      name: $fn.value.pgettext('MeterSensorDataBrowser', 'Timestamp'),
      formatter: {
        name: 'datetime',
      },
    },
    {
      field: 'rentals',
      name: $fn.value.pgettext('MeterSensorDataBrowser', 'Rentals'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        separator: ', ',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'name',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'tenants',
      name: $fn.value.pgettext('MeterSensorDataBrowser', 'Tenants'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        separator: ', ',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'name',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    timeTo: timeTo.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_timeTo = async () => {
    return new Date();
  };
  const $$load_ds = async () => {
    return $fn.value.slickDataSource('integro_api/list_auto_meter_readings', {
      parameters: getQueryParameters,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      {
        name: 'timeTo',
        cache: { type: 'local', key: 'SensorDataBrowser.timeTo' },
      },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { timeTo: $$load_timeTo, ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      timeTo,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('MeterSensorDataBrowser', 'Auto Readings'),
      icon: 'fa-ethernet',
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_r5pvvajk2_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_wr7fx3nnu_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_closeModal_r5pvvajk2 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_r5pvvajk2_getActionArgs,
    });
  const inst_evh_7315092382398562_close_58kdwoilj =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_r5pvvajk2,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_58kdwoilj],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_420tkgmjo =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_94ti69763 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_420tkgmjo,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_94ti69763],
    event: 'reload',
  });
  const act_2248226175642056_setData_wr7fx3nnu = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_wr7fx3nnu_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_rxu5dqze4 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_wr7fx3nnu,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_rxu5dqze4],
      event: 'selectedRowsChanged',
    });

  const actions = {
    inst_evh_7315092382398562_close_58kdwoilj,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_94ti69763,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_rxu5dqze4,
    inst_evh_2248226175642056_selectedRowsChanged,
  };

  const evh_7315092382398562_close_58kdwoilj = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_58kdwoilj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_94ti69763 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_94ti69763.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_rxu5dqze4 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_rxu5dqze4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="SensorDataBrowser"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="autoReadings"
      :headerTitle="pgettext('MeterSensorDataBrowser', 'Auto Readings')"
      :items="ds"
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'meter-sensor-data')"
      checkbox-row-selection
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      v-if="loaded"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <ItpFormDatetime
            name="time_to"
            v-model="timeTo"
            size="xs"
            class="w-auto"
          >
          </ItpFormDatetime>
          <JobHandlerControl
            size="md"
            jobType="create_meter_readings"
            load-current
            :payload="() => onCreateMeterReadingsPayload()"
            icon="material-symbols:cloud-sync-outline-rounded"
            :text="pgettext('SensorDataBrowser|Button', 'Create readings')"
            :disabled="!selectedItems || !selectedItems.length"
          >
          </JobHandlerControl>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
